import {
    createContext,
    MutableRefObject,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useSyncExternalStore,
} from "react";

type IGetSet<S> = {
    get: () => S;
    set: (updater: Partial<S> | ((prev: S) => Partial<S>)) => void;
};

type TCaseEvent = Record<string, (...args: any[]) => void>;

type IEvent<S, E extends TCaseEvent> = {
    [Name in keyof E]: (
        ...props: [...Parameters<E[Name]>, IGetSet<S> & { event: () => E }]
    ) => void;
};

export function createFastContext<S, CaseEvent extends TCaseEvent = {}>(initialState: S) {
    type WrappedEvent = {
        [Name in keyof CaseEvent]: (...args: Parameters<CaseEvent[Name]>) => void;
    };

    type StoreType = IGetSet<S> & {
        subscribe: (callback: () => void) => () => void;
        eventRef: MutableRefObject<IEvent<S, CaseEvent> | undefined>;
        wrappedEvent: WrappedEvent | undefined;
    };

    const StoreContext = createContext<StoreType | null>(null);

    function Provider({
        children,
        props = {},
        event,
    }: {
        children: ReactNode;
        props?: Partial<S>;
        event?: IEvent<S, CaseEvent>;
    }) {
        const store = useRef<S>({ ...initialState, ...props });
        const subscribers = useRef(new Set<() => void>());
        const prevPropsRef = useRef<Partial<S>>(props);
        const eventRef = useRef<IEvent<S, CaseEvent> | undefined>(event);
        eventRef.current = event;

        const get = useCallback(() => store.current, []);

        const set = useCallback((updater: Partial<S> | ((prev: S) => Partial<S>)) => {
            const nextState =
                typeof updater === "function" ? updater(store.current) : updater;

            if (!nextState || typeof nextState !== "object") return;

            const nextStore = { ...store.current, ...nextState };

            let changed = false;
            for (const key in nextState) {
                if (store.current[key as keyof S] !== nextStore[key as keyof S]) {
                    changed = true;
                    break;
                }
            }

            if (!changed) return;

            store.current = nextStore;
            subscribers.current.forEach((cb) => cb());
        }, []);

        const subscribe = useCallback((cb: () => void) => {
            subscribers.current.add(cb);
            return () => subscribers.current.delete(cb);
        }, []);

        const eventKeysSignature = event
            ? (Object.keys(event) as Array<keyof CaseEvent>).sort().join("\u0000")
            : "__NO_EVENT__";

        const wrappedEvent = useMemo(() => {
            if (eventKeysSignature === "__NO_EVENT__") return undefined;

            const result = {} as WrappedEvent;

            const createEvent = () => result as CaseEvent;
            const eventKeys = eventKeysSignature.split("\u0000") as Array<keyof CaseEvent>;

            eventKeys.forEach((key) => {
                result[key] = ((...payload: Parameters<CaseEvent[typeof key]>) => {
                    const latestEvent = eventRef.current;
                    if (!latestEvent || !latestEvent[key]) {
                        throw new Error("Event not created");
                    }

                    latestEvent[key](...payload, {
                        get,
                        set,
                        event: createEvent,
                    });
                }) as WrappedEvent[typeof key];
            });

            return result;
        }, [eventKeysSignature, get, set]);

        const contextValue = useMemo(
            () => ({
                get,
                set,
                subscribe,
                eventRef,
                wrappedEvent,
            }),
            [get, set, subscribe, wrappedEvent]
        );

        useEffect(() => {
            const prev = prevPropsRef.current;
            const nextStore = { ...store.current };
            let isChanged = false;

            Object.keys(props).forEach((key) => {
                if ((props as any)[key] === (prev as any)[key]) return;

                (nextStore as any)[key] = (props as any)[key];
                isChanged = true;
            });

            Object.keys(prev).forEach((key) => {
                if (key in props) return;

                if (key in (initialState as any)) {
                    (nextStore as any)[key] = (initialState as any)[key];
                } else {
                    delete (nextStore as any)[key];
                }

                isChanged = true;
            });

            if (!isChanged) return;

            store.current = nextStore;
            subscribers.current.forEach((cb) => cb());
            prevPropsRef.current = props;
        }, [props]);

        return (
            <StoreContext.Provider value={contextValue}>
                {children}
            </StoreContext.Provider>
        );
    }

    function useStore<Selected>(
        selector: (s: S) => Selected,
        equalityFn: (prev: Selected, next: Selected) => boolean = Object.is
    ): [Selected, IGetSet<S>["set"]] {
        const ctx = useContext(StoreContext);
        if (!ctx) throw new Error("useStore must be inside a Provider");
        const selectedRef = useRef<Selected>(selector(ctx.get()));

        const selected = useSyncExternalStore(
            ctx.subscribe,
            () => {
                const nextSelected = selector(ctx.get());

                if (equalityFn(selectedRef.current, nextSelected)) {
                    return selectedRef.current;
                }

                selectedRef.current = nextSelected;
                return nextSelected;
            },
            () => selector(initialState)
        );

        return [selected, ctx.set];
    }

    function GetStore(): () => S {
        const ctx = useContext(StoreContext);
        if (!ctx) throw new Error("getStore must be used inside Provider");
        return ctx.get;
    }

    function SetStore(): IGetSet<S>["set"] {
        const ctx = useContext(StoreContext);
        if (!ctx) throw new Error("setStore must be used inside Provider");
        return ctx.set;
    }

    function CallEvent(): WrappedEvent {
        const ctx = useContext(StoreContext);
        if (!ctx) throw new Error("Store not found");

        if (!ctx.wrappedEvent) {
            throw new Error("Event not created");
        }

        return ctx.wrappedEvent;
    }

    return {
        Provider,
        useStore,
        getStore: GetStore,
        setStore: SetStore,
        callEvent: CallEvent,
    };
}
